if (!customElements.get('pull-quote-video-player')) {
  customElements.define(
    'pull-quote-video-player',
    class PullQuoteVideoPlayer extends HTMLElement {
      #playButton = null;
      #video = null;
      #embed = null;
      #iframe = null;
      #poster = null;
      #mode = null;
      #userInitiated = false;
      #onPlayClick = null;
      #onPlayKeydown = null;
      #onVideoPlay = null;
      #onVideoEnded = null;

      static #EMBED_ALLOW =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

      connectedCallback() {
        this.#playButton = this.querySelector('[data-play-button]');
        this.#video = this.querySelector('video');
        this.#embed = this.querySelector('[data-embed]');
        this.#poster = this.querySelector('[data-poster]');
        this.#mode = this.#video ? 'hosted' : this.#embed ? 'embed' : null;

        if (!this.#playButton || !this.#mode) {
          return;
        }

        this.#onPlayClick = this.#handlePlay.bind(this);
        this.#onPlayKeydown = this.#handlePlayKeydown.bind(this);
        this.#onVideoPlay = this.#handleVideoPlay.bind(this);
        this.#onVideoEnded = this.#handleVideoEnded.bind(this);

        this.#playButton.addEventListener('click', this.#onPlayClick);
        this.#playButton.addEventListener('keydown', this.#onPlayKeydown);

        if (this.#mode === 'hosted') {
          this.#video.addEventListener('play', this.#onVideoPlay);
          this.#video.addEventListener('ended', this.#onVideoEnded);
        }

        this.#resetToIdle();
      }

      disconnectedCallback() {
        if (!this.#playButton) {
          return;
        }

        this.#playButton.removeEventListener('click', this.#onPlayClick);
        this.#playButton.removeEventListener('keydown', this.#onPlayKeydown);

        if (this.#mode === 'hosted' && this.#video) {
          this.#video.removeEventListener('play', this.#onVideoPlay);
          this.#video.removeEventListener('ended', this.#onVideoEnded);
        }
      }

      #handlePlayKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        this.#handlePlay();
      }

      async #handlePlay() {
        if (this.#playButton?.disabled) {
          return;
        }

        if (this.#mode === 'embed') {
          this.#handleEmbedPlay();
          return;
        }

        if (!this.#video) {
          return;
        }

        this.#userInitiated = true;
        this.#showPlayingState();
        this.#video.controls = true;

        try {
          await this.#video.play();
        } catch {
          this.#resetToIdle();
        }
      }

      #handleEmbedPlay() {
        if (!this.#embed) {
          return;
        }

        const embedSrc = this.#embed.dataset.embedSrc;

        if (!embedSrc) {
          return;
        }

        this.#userInitiated = true;
        this.#showPlayingState();

        if (!this.#iframe) {
          this.#iframe = document.createElement('iframe');
          this.#iframe.className = 'pull-quote-video__embed-frame';
          this.#iframe.setAttribute('allow', PullQuoteVideoPlayer.#EMBED_ALLOW);
          this.#iframe.setAttribute('frameborder', '0');
          this.#iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
          this.#iframe.setAttribute('title', this.#playButton.getAttribute('aria-label') || 'Video');
          this.#embed.appendChild(this.#iframe);
        }

        const separator = embedSrc.includes('?') ? '&' : '?';
        this.#iframe.src = `${embedSrc}${separator}autoplay=1`;
        this.#embed.removeAttribute('hidden');
      }

      #handleVideoPlay() {
        if (!this.#userInitiated) {
          this.#video.pause();
          return;
        }

        this.#showPlayingState();
      }

      #handleVideoEnded() {
        this.#video.pause();
        this.#video.currentTime = 0;
        this.#resetToIdle();
      }

      #resetToIdle() {
        this.#userInitiated = false;
        this.classList.remove('pull-quote-video-player--playing');

        if (this.#mode === 'hosted' && this.#video) {
          this.#video.autoplay = false;
          this.#video.removeAttribute('autoplay');
          this.#video.controls = false;
          this.#video.removeAttribute('controls');
          this.#video.pause();
          this.#video.currentTime = 0;
        }

        if (this.#mode === 'embed' && this.#embed) {
          if (this.#iframe) {
            this.#iframe.removeAttribute('src');
            this.#iframe.remove();
            this.#iframe = null;
          }

          this.#embed.setAttribute('hidden', '');
        }

        this.#poster?.removeAttribute('hidden');
        this.#poster?.removeAttribute('aria-hidden');
        this.#playButton.removeAttribute('hidden');
        this.#playButton.removeAttribute('aria-hidden');
      }

      #showPlayingState() {
        this.classList.add('pull-quote-video-player--playing');

        this.#poster?.setAttribute('hidden', '');
        this.#poster?.setAttribute('aria-hidden', 'true');
        this.#playButton.setAttribute('hidden', '');
        this.#playButton.setAttribute('aria-hidden', 'true');
      }
    }
  );
}
